import { defineStore } from 'pinia';
import { getHandlungen } from '@/api/handlungen/handlungenApi.ts';
import type { Handlung, InteractionStatus } from '@/api/handlungen/types';
import { computed, ref } from "vue";
import { groupBy } from "lodash-es";

export const useHandlungStore = defineStore('handlung', () => {
  const handlungen = ref<Map<number, Handlung>>(new Map());
  const initialized = ref<boolean>(false);
  const loading = ref<boolean>(false);
  const errorMsg = ref<string|null>(null);

  const initializing = computed<boolean>(() => !initialized.value || loading.value);
  const handlungenByState = computed<Partial<Record<InteractionStatus, Handlung[]>>>(() => {
    return groupBy<Handlung>(Array.from(handlungen.value.values()), (a) => a.interaction_status);
  });

  const priorityHandlung = computed<null|Handlung>(() => handlungenByState.value.open?.reduce(
    (max: null|Handlung, handlung: Handlung) => handlung.priority > (max?.priority ?? 0) ? handlung : max, null,
  ) ?? null);

  function getHandlungById(id: number): Handlung|null {
    return handlungen.value.get(id) ?? null;
  }

  function updateHandlung(handlung: Handlung): void {
    handlungen.value.set(handlung.id, handlung);
  }

  function setHandlungen(data: Handlung[]): void {
    handlungen.value.clear();
    data.forEach((handlung) => handlungen.value.set(handlung.id, handlung));
  }

  async function fetchHandlungenSilently() {
    const result = await getHandlungen();
    if (result instanceof Error) return;

    setHandlungen(result);
  }

  async function fetchHandlungen() {
    loading.value = true;
    errorMsg.value = null;
    const result = await getHandlungen();
    if (result instanceof Error) {
      errorMsg.value = "Unerwarteter Fehler beim Laden der Handlungen"
    } else {
      setHandlungen(result);
    }
    loading.value = false;
    initialized.value = true;
  }

  function $reset() {
    handlungen.value.clear();
    loading.value = false;
    errorMsg.value = null;
  }

  return {
    handlungen,
    errorMsg,
    loading,
    initializing,

    handlungenByState,
    priorityHandlung,

    $reset,
    updateHandlung,
    fetchHandlungen,
    fetchHandlungenSilently,
    getHandlungById,
  }
});
